import React, { useEffect, useState } from 'react';
import { Button, Card, DatePicker, Descriptions, Input, Modal, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { listMeituanCancelLogs, type MeituanCancelLog as MeituanCancelLogRecord } from '../services/meituanCancelLogService';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const processStatusMap: Record<number, { text: string; color: string }> = {
  0: { text: '待处理', color: 'orange' },
  1: { text: '已处理', color: 'green' },
  2: { text: '处理失败', color: 'red' },
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : value;
};

const formatJSON = (value?: string) => {
  if (!value) return '-';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const extractOrderId = (record: MeituanCancelLogRecord) => {
  for (const raw of [record.rawParams, record.rawBody, record.rawQuery]) {
    if (!raw) continue;
    const matched = raw.match(/orderId["=:]+\\?"?([^"',&}]+)/);
    if (matched?.[1]) {
      return matched[1].replace(/\\/g, '').replace(/^"+|"+$/g, '');
    }
  }
  return '-';
};

const MeituanCancelLog: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<MeituanCancelLogRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [meituanOrderId, setMeituanOrderId] = useState('');
  const [processStatus, setProcessStatus] = useState<string>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentLog, setCurrentLog] = useState<MeituanCancelLogRecord | null>(null);

  const loadData = async (nextPage = page, nextPageSize = pageSize) => {
    setLoading(true);
    try {
      const res = await listMeituanCancelLogs({
        page: nextPage,
        pageSize: nextPageSize,
        meituanOrderId: meituanOrderId || undefined,
        processStatus,
        startTime: dateRange?.[0]?.format('YYYY-MM-DD 00:00:00'),
        endTime: dateRange?.[1]?.format('YYYY-MM-DD 23:59:59'),
      });
      setLogs(res.data.list || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error('加载美团取消日志失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    void loadData(1, pageSize);
  };

  const handleReset = () => {
    setMeituanOrderId('');
    setProcessStatus(undefined);
    setDateRange(null);
    setPage(1);
    setTimeout(() => void loadData(1, pageSize), 0);
  };

  const columns: ColumnsType<MeituanCancelLogRecord> = [
    {
      title: '美团订单ID',
      key: 'orderId',
      width: 170,
      render: (_, record) => <Tooltip title={extractOrderId(record)}><Text>{extractOrderId(record)}</Text></Tooltip>,
    },
    { title: '消息类型', dataIndex: 'messageType', key: 'messageType', width: 110, render: value => <Tag color="blue">{value || '5810001'}</Tag> },
    { title: '业务ID', dataIndex: 'businessId', key: 'businessId', width: 100, render: value => value || '-' },
    {
      title: '处理状态',
      dataIndex: 'processStatus',
      key: 'processStatus',
      width: 110,
      render: value => {
        const item = processStatusMap[value] || { text: value ?? '-', color: 'default' };
        return <Tag color={item.color}>{item.text}</Tag>;
      },
    },
    {
      title: '处理说明',
      dataIndex: 'processMessage',
      key: 'processMessage',
      width: 360,
      ellipsis: true,
      render: value => value ? <Tooltip title={value}><Text>{value}</Text></Tooltip> : '-',
    },
    { title: '推送时间', dataIndex: 'createdAt', key: 'createdAt', width: 170, render: value => formatDateTime(value) },
    {
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setCurrentLog(record);
            setDetailVisible(true);
          }}
        >
          详情
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>美团取消日志</Title>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input placeholder="美团订单ID" value={meituanOrderId} onChange={event => setMeituanOrderId(event.target.value)} allowClear style={{ width: 180 }} />
          <Select placeholder="处理状态" value={processStatus} onChange={setProcessStatus} allowClear style={{ width: 120 }}>
            <Select.Option value="0">待处理</Select.Option>
            <Select.Option value="1">已处理</Select.Option>
            <Select.Option value="2">处理失败</Select.Option>
          </Select>
          <RangePicker value={dateRange} onChange={setDateRange} />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
        </Space>
      </Card>
      <Card>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={logs}
          scroll={{ x: 1100 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: value => `共 ${value} 条`,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize || 10);
            },
          }}
        />
      </Card>
      <Modal title="美团取消日志详情" open={detailVisible} onCancel={() => setDetailVisible(false)} footer={null} width={900}>
        {currentLog && (
          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="美团订单ID">{extractOrderId(currentLog)}</Descriptions.Item>
              <Descriptions.Item label="消息类型">{currentLog.messageType || '-'}</Descriptions.Item>
              <Descriptions.Item label="业务ID">{currentLog.businessId || '-'}</Descriptions.Item>
              <Descriptions.Item label="处理状态">{processStatusMap[currentLog.processStatus]?.text || currentLog.processStatus}</Descriptions.Item>
              <Descriptions.Item label="处理说明" span={2}>{currentLog.processMessage || '-'}</Descriptions.Item>
              <Descriptions.Item label="推送时间">{formatDateTime(currentLog.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="更新时间">{formatDateTime(currentLog.updatedAt)}</Descriptions.Item>
            </Descriptions>
            <Card title="合并参数 rawParams" size="small">
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{formatJSON(currentLog.rawParams)}</pre>
            </Card>
            <Card title="原始 Body" size="small">
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{formatJSON(currentLog.rawBody)}</pre>
            </Card>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default MeituanCancelLog;
